import { Matrix3, PerspectiveCamera, Vector3 } from 'three'
import { uvToWorld, worldToUv } from '@/lib/site-viewer/utils'
import { SPHERE_RADIUS, CAMERA_DISTANCE_TO_TARGET, MAX_FOV } from '@/lib/site-viewer/constants'

// Uses UV coordinates (latitude and longitude each with range of 0 to 1)

const pt = new Vector3()
export function normalizedCoordsToSphereSpace(point: Point, camera: PerspectiveCamera): Point {
  // get UV coordinates from normalized device coordinates
  const normalized = { x: (point.x * 2) / camera.aspect, y: -point.y * 2 }
  pt.set(normalized.x, normalized.y, 1).unproject(camera)
  const uv = worldToUv(pt.normalize())
  return { x: uv.x, y: 1 - uv.y }
}

export function sphereSpaceToNormalizedCoords(point: Point, camera: PerspectiveCamera): Point {
  // project UV into normalized device coordinates
  const pt = uvToWorld({ x: point.x, y: 1 - point.y })
    .multiplyScalar(SPHERE_RADIUS - 10)
    .project(camera)
  return { x: (pt.x / 2) * camera.aspect, y: -pt.y / 2 }
}

// declare in module scope for memory management
const top = Math.tan(MAX_FOV / 2) * 4
const point = new Vector3()
const pos = new Vector3()
const m = new Matrix3()

/**
 * @param pt Normalized screen coordinate clicked
 * @param cameraTarget UV coordinates of the current camera target
 */
export function screenToUv(pt: Point, cameraTarget: Point): Point {
  const ptx = cameraTarget.x * Math.PI * 2
  const pty = cameraTarget.y * Math.PI
  const siny = Math.sin(pty)

  pos.set(-Math.cos(ptx) * siny, -Math.cos(pty), -Math.sin(ptx) * siny).multiplyScalar(-CAMERA_DISTANCE_TO_TARGET)
  point.set(top * pt.x * 2, top * pt.y * 2, -4 / 0.974925)

  // rotation matrix
  const { x, y, z } = pos
  const zl = Math.sqrt(x * x + y * y + z * z)
  const xl = Math.sqrt(x * x + z * z)
  point.applyMatrix3(m.set(z / xl, (-y * x) / (xl * zl), x / zl, 0, xl / zl, y / zl, -x / xl, (-y * z) / (xl * zl), z / zl))

  point.add(pos).normalize()

  // world to uv
  const u = Math.atan2(point.z, point.x) / (2 * Math.PI) + 0.5
  const v = Math.asin(point.y) / Math.PI + 0.5

  return { x: u, y: v }
}

/**
 * @param uv UV coordinates of the point to draw
 * @param cameraTarget UV coordinates of the current camera target
 */
export function uvToScreen(uv: Point, cameraTarget: Point): Point {
  {
    // project UV into world coordinates
    const ptx = uv.x * Math.PI * 2
    const pty = uv.y * Math.PI
    const siny = Math.sin(pty)
    point.set(-Math.cos(ptx) * siny, -Math.cos(pty), -Math.sin(ptx) * siny).multiplyScalar(1e10)
  }

  // project UV into world coordinates
  const ptx = cameraTarget.x * Math.PI * 2
  const pty = cameraTarget.y * Math.PI
  const sinx = Math.sin(ptx),
    siny = Math.sin(pty),
    cosx = Math.cos(ptx),
    cosy = Math.cos(pty)

  // Set to camera position direction. It is directly opposite the camera target
  // through the origin (we use camera.lookAt(0,0,0) to point it at the target)
  pos.set(cosx * siny, cosy, sinx * siny)

  // Since the camera is positioned behind the origin (0,0,0) and the target
  // is in front, we want to know if the point vector and the camera position
  // vector are pointed at the same hemisphere. If yes then the point is behind
  // the camera and shouldn't be projected.
  if (point.dot(pos) > 0) return { x: NaN, y: NaN }

  // rotation matrix
  m.set(sinx, 0, -cosx, -cosy * cosx, siny, -sinx * cosy, siny * cosx, cosy, sinx * siny)
  point.sub(pos.multiplyScalar(CAMERA_DISTANCE_TO_TARGET)).applyMatrix3(m)
  point.multiplyScalar(-2 / (top * point.z))

  return { x: point.x, y: point.y }
}
